// Recover from a deploy that happened while a tab was open.
//
// Routes are lazy chunks with content-hashed names. A new deploy replaces them,
// so an old tab that navigates to a not-yet-loaded route asks for a chunk that
// no longer exists, and the import fails ("Failed to fetch dynamically imported
// module"). Loading the new index.html fixes it, so reload, once: a guard stops
// a loop when a chunk is genuinely broken. Public-form drafts survive this
// (they're saved on pagehide, see lib/draft.js).

const GUARD_KEY = "nsp_chunk_reload_at"
const GUARD_MS = 60_000

const CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk \S+ failed/i

/** True for the errors browsers raise when a lazily imported chunk can't be loaded. */
export function isChunkLoadError(err) {
  return CHUNK_ERROR.test(String(err?.message ?? err ?? ""))
}

/**
 * Reload (optionally at `target`) unless we already did so within the last
 * minute. Returns whether a reload was started. Storage can be unavailable —
 * then we still reload, and the browser's own history protects against loops.
 */
export function reloadForNewDeploy(target, { storage = globalThis.sessionStorage, now = Date.now(), location = globalThis.location } = {}) {
  try {
    const last = Number(storage?.getItem(GUARD_KEY))
    if (last && now - last < GUARD_MS) return false
    storage?.setItem(GUARD_KEY, String(now))
  } catch { /* storage blocked — reload anyway */ }
  if (target) location.assign(target)
  else location.reload()
  return true
}

/** Wire both failure paths: Vite's preload helper and the router's lazy-route import. */
export function installDeployRecovery(router) {
  // The route being navigated to, so either failure path below reloads into the
  // page the user asked for rather than the one they were leaving.
  let pending = null
  router.beforeEach((to) => { pending = to.fullPath })
  router.afterEach(() => { pending = null })

  // Vite fires this when preloading a chunk (or its dependencies) fails. During
  // a navigation leave it alone: the error then reaches router.onError below,
  // which reloads into the target. Preventing it here would make the lazy route
  // resolve to nothing and the router throw "Couldn't resolve component".
  // Outside a navigation (some other dynamic import), reload right here.
  window.addEventListener("vite:preloadError", (e) => {
    if (pending === null && reloadForNewDeploy()) e.preventDefault()
  })
  // A lazy route's import() failing during navigation.
  router.onError((err, to) => {
    if (isChunkLoadError(err)) reloadForNewDeploy(to?.fullPath ?? pending ?? undefined)
  })
}

// ---------------------------------------------------------------------------
// A deploy while the tab stays open, with nothing failing.
//
// The recovery above only fires when an old chunk is gone, and the service
// worker keeps old chunks cached, so after a deploy an open tab went on
// running — and navigating in — the previous build until a manual refresh.
// So: remember which build this page started with (/api/version: commit and
// build time), look again every few minutes, when the tab is shown again, and
// on navigation (at most once a minute); once the server runs a different
// build, the next navigation is a full page load. Leave guards (unsaved edits)
// have already run by then, so nothing is lost silently.
// ---------------------------------------------------------------------------

/** "commit|built" of a /api/version answer, or null. */
export function buildKey(v) {
  return v && v.commit ? `${v.commit}|${v.built}` : null
}

const defaultFetchVersion = async () => {
  const r = await fetch("/api/version", { cache: "no-store", credentials: "same-origin" })
  return r.ok ? r.json() : null
}

export function installNewVersionReload(router, {
  fetchVersion = defaultFetchVersion, now = () => Date.now(), location = globalThis.location,
  everyMs = 5 * 60_000, onNavigationAfterMs = 60_000, doc = globalThis.document,
} = {}) {
  let started = null       // the build this page runs
  let outdated = false
  let lastCheck = -Infinity
  const check = async () => {
    lastCheck = now()
    try {
      const key = buildKey(await fetchVersion())
      if (!key) return
      if (started === null) started = key
      else if (key !== started) outdated = true
    } catch { /* offline or restarting: try again later */ }
  }
  const ready = check()
  const timer = setInterval(check, everyMs)
  doc?.addEventListener?.("visibilitychange", () => { if (doc.visibilityState === "visible") check() })
  router.beforeEach(() => { if (now() - lastCheck >= onNavigationAfterMs) check() })   // not awaited: no delay
  router.beforeResolve((to, from) => {
    if (outdated && from.matched.length) {      // not on the very first navigation
      location.assign(to.fullPath)
      return false
    }
  })
  return { ready, check, stop: () => clearInterval(timer), get outdated() { return outdated } }
}
